import { ChevronRight, ChevronDown, CornerDownRight } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  EmptyValue,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/common/StatusBadge';
import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import type { EquipmentTreeNode } from '@/api/services/equipment.api';
import { type FlatRow } from '@/components/common/equipment-tree';

interface EquipmentTreeTableProps {
  rows: FlatRow[];
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onOpen: (node: EquipmentTreeNode) => void;
  onFocus: (node: EquipmentTreeNode) => void;
  /** Node the view is currently rooted at — its own Focus action is hidden. */
  focusedId?: string | null;
}

/**
 * Hierarchy rendered as a table rather than nested cards.
 *
 * Rows are a uniform height, so depth has to be carried by the first column
 * alone: indentation, guide lines, and a disclosure chevron. Everything else
 * lines up in real columns, which is the point — values become comparable
 * across siblings, which nested cards can never manage.
 */
export function EquipmentTreeTable({
  rows,
  expanded,
  onToggle,
  onOpen,
  onFocus,
  focusedId,
}: EquipmentTreeTableProps) {
  return (
    <div className={PANEL}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[34%]">Asset</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Hours</TableHead>
            <TableHead className="text-right">Work Orders</TableHead>
            <TableHead className="w-[1%]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ node, depth, hasChildren, guides }) => {
            const isOpen = expanded.has(node.id);
            const workOrders = node._count?.workOrders ?? 0;
            const childCount = node._count?.children ?? node.children?.length ?? 0;

            return (
              <TableRow
                key={node.id}
                onClick={() => onOpen(node)}
                className="cursor-pointer group"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onOpen(node);
                  // Left/Right walk the tree the way a tree view should.
                  if (e.key === 'ArrowRight' && hasChildren && !isOpen) onToggle(node.id);
                  if (e.key === 'ArrowLeft' && hasChildren && isOpen) onToggle(node.id);
                }}
              >
                <TableCell>
                  <div className="flex items-center">
                    {/* Depth. Guides only render where an ancestor continues,
                        so the last child of a branch closes cleanly. */}
                    {guides.map((continues, i) => (
                      <span
                        key={i}
                        aria-hidden
                        className={cn('h-8 w-5 shrink-0', continues && 'border-l border-border')}
                      />
                    ))}
                    {depth > 0 && (
                      <CornerDownRight
                        aria-hidden
                        className="mr-1 h-3.5 w-3.5 shrink-0 text-muted-foreground/60"
                      />
                    )}
                    {hasChildren ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggle(node.id);
                        }}
                        aria-expanded={isOpen}
                        aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${node.name}`}
                        className="mr-1 shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {isOpen ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                    ) : (
                      // Keeps leaf names aligned with their expandable siblings.
                      <span aria-hidden className="mr-1 h-5 w-5 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-foreground">
                        {node.name}
                      </div>
                      <div className="mt-0.5 truncate font-mono text-xs text-muted-foreground">
                        {node.assetNumber}
                        {hasChildren && (
                          <span className="ml-2 font-sans">
                            · {childCount} sub-{childCount === 1 ? 'asset' : 'assets'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs font-normal">
                    {node.category.replace(/_/g, ' ')}
                  </Badge>
                </TableCell>
                <TableCell>{node.location?.name ?? <EmptyValue />}</TableCell>
                <TableCell>
                  <StatusBadge status={node.status} size="sm" />
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {node.operatingHours != null ? (
                    node.operatingHours.toLocaleString()
                  ) : (
                    <EmptyValue />
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {/* Explicit comparison, not `count && …`: a zero count would
                      otherwise render as a bare "0" with no label. */}
                  {workOrders > 0 ? workOrders : <EmptyValue />}
                </TableCell>
                <TableCell className="whitespace-nowrap text-right">
                  {hasChildren && node.id !== focusedId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        onFocus(node);
                      }}
                    >
                      Focus
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpen(node);
                    }}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
